import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  DeleteSweep,
  Close,
} from '@mui/icons-material';
import { generateWhiteboardTokenAPI } from '../../services/agora';

const Whiteboard = ({ sessionId, appIdentifier, region = 'us-sv', onClose }) => {
  const containerRef = useRef(null);
  const fastboardRef = useRef(null);
  const uiRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    let fastboard = null;
    let ui = null;
    let resizeObserver = null;

    const initializeFastboard = async () => {
      try {
        if (!appIdentifier) {
          if (isMounted) {
            setError('Whiteboard is not configured. Please set REACT_APP_AGORA_WHITEBOARD_APP_IDENTIFIER environment variable.');
            setLoading(false);
          }
          return;
        }

        if (!containerRef.current) {
          throw new Error('Whiteboard container element not found');
        }

        // Dynamically import Fastboard to avoid loading it on all pages
        // According to Fastboard docs: https://github.com/netless-io/fastboard
        const { createFastboard, createUI } = await import('@netless/fastboard');

        // Fetch whiteboard room token from backend
        const response = await generateWhiteboardTokenAPI(sessionId);
        
        if (!response || !response.data) {
          throw new Error('Invalid response from whiteboard token API');
        }
        
        const { uuid, roomToken, uid } = response.data;
        
        if (!uuid || !roomToken) {
          throw new Error(`Missing whiteboard credentials: uuid=${uuid ? 'present' : 'missing'}, roomToken=${roomToken ? 'present' : 'missing'}`);
        }

        // Create Fastboard instance
        // According to Fastboard docs: https://github.com/netless-io/fastboard
        fastboard = await createFastboard({
          sdkConfig: {
            appIdentifier: appIdentifier,
            region: region,
          },
          joinRoom: {
            uuid: uuid,
            roomToken: roomToken,
            uid: uid || `user-${Date.now()}`,
          },
          managerConfig: {
            cursor: true,
          },
        });

        if (!isMounted) {
          fastboard.destroy();
          return;
        }

        fastboardRef.current = fastboard;

        // Create and mount Fastboard UI to container
        // According to Fastboard docs, we use createUI to mount the whiteboard
        if (containerRef.current) {
          ui = createUI(fastboard, containerRef.current);
          uiRef.current = ui;
          
          // Set up resize observer to ensure whiteboard fills the container
          if (window.ResizeObserver && containerRef.current) {
            resizeObserver = new ResizeObserver(() => {
              if (ui && isMounted && containerRef.current) {
                try {
                  // Update UI size when container resizes
                  if (ui.update) {
                    ui.update({});
                  }
                } catch (err) {
                  // Ignore resize errors
                }
              }
            });
            resizeObserver.observe(containerRef.current);
          }
        }
      } catch (err) {
        console.error('Error initializing Fastboard:', err);
        if (isMounted) {
          const errorMessage = err.response?.data?.msg || err.message || 'Failed to initialize whiteboard';
          setError(errorMessage);
          setLoading(false);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (!sessionId) {
      setError('Session ID is required');
      setLoading(false);
      return;
    }

    if (!appIdentifier) {
      setError('Whiteboard is not configured. Please set REACT_APP_AGORA_WHITEBOARD_APP_IDENTIFIER environment variable.');
      setLoading(false);
      return;
    }

    // Wait a bit for DOM to be ready
    const initTimeout = setTimeout(() => {
      initializeFastboard();
    }, 100);

    const containerElement = containerRef.current;

    return () => {
      clearTimeout(initTimeout);
      isMounted = false;
      if (resizeObserver && containerElement) {
        try {
          resizeObserver.unobserve(containerElement);
        } catch (err) {
          // Ignore errors
        }
        resizeObserver.disconnect();
      }
      if (ui && uiRef.current === ui) {
        try {
          ui.destroy();
        } catch (err) {
          console.error('Error destroying Fastboard UI:', err);
        }
        uiRef.current = null;
      }
      if (fastboard && fastboardRef.current === fastboard) {
        try {
          fastboard.destroy();
        } catch (err) {
          console.error('Error destroying Fastboard:', err);
        }
        fastboardRef.current = null;
      }
    };
  }, [sessionId, appIdentifier, region]);

  const handleClear = () => {
    if (fastboardRef.current) {
      try {
        const room = fastboardRef.current.room;
        if (room && typeof room.cleanCurrentScene === 'function') {
          room.cleanCurrentScene();
        }
      } catch (err) {
        console.error('Error clearing whiteboard:', err);
      }
    }
  };

  const isConfigError = error && (error.includes('not configured') || error.includes('environment variable'));

  return (
    <Paper
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'white',
        borderRadius: 2,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Floating close button */}
      {!loading && !error && onClose && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 20,
            bgcolor: 'rgba(0, 0, 0, 0.6)',
            borderRadius: '50%',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.8)',
            },
          }}
        >
          <Tooltip title="Close whiteboard">
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Clear button - Fastboard has its own undo/redo buttons */}
      {!loading && !error && fastboardRef.current && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: onClose ? 64 : 16,
            zIndex: 20,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: 1,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          }}
        >
          <Tooltip title="Clear whiteboard">
            <IconButton
              size="small"
              onClick={handleClear}
              sx={{
                '&:hover': {
                  bgcolor: 'rgba(255, 0, 0, 0.1)',
                },
              }}
            >
              <DeleteSweep fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Box
        ref={containerRef}
        sx={{
          flex: 1,
          width: '100%',
          height: '100%',
          position: 'relative',
        }}
      />
      
      {/* Loading overlay */}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            zIndex: 10,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      
      {/* Error overlay */}
      {error && !loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            zIndex: 10,
            p: 2,
          }}
        >
          <Alert severity={isConfigError ? "info" : "error"}>
            {error}
            {isConfigError && (
              <Box sx={{ mt: 1, fontSize: '0.875rem', color: 'text.secondary' }}>
                The whiteboard feature requires Agora Whiteboard credentials. Video call functionality is still available.
              </Box>
            )}
          </Alert>
        </Box>
      )}
    </Paper>
  );
};

export default Whiteboard;
